import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Grid, Chip, InputAdornment,
  alpha, Avatar, Skeleton, Checkbox, ToggleButton, ToggleButtonGroup,
  LinearProgress, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Stepper, Step, StepLabel, Menu, MenuItem, Divider,
  List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import {
  Search, Receipt, CalendarToday, CheckCircle, Business, People,
  PlayArrow, Refresh, History, TrendingUp, Delete, Close, Visibility,
  Payment as PaymentIcon, Settings as SettingsIcon, Email, EditNote,
  WarningAmber, Add, ArrowForward, ArrowBack, Description, Info
} from '@mui/icons-material';
import { providerInvoicesAPI, contractorInvoicesAPI, collectionsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { fullName } from '../../utils/formatters';
import { useTableSort } from '../../components/useTableSort';

const fmt = v => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);
const TabPanel = ({ children, value, index }) => value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const statusColor = (s) => {
  const map = { generated: '#3b82f6', sent: '#f59e0b', partial: '#f97316', paid: '#10b981', overdue: '#ef4444', cancelled: '#94a3b8' };
  return map[s] || '#64748b';
};
const paymentChip = (inv) => {
  const paid = parseFloat(inv.amountPaid) || 0;
  const total = parseFloat(inv.total) || 0;
  if (paid >= total && total > 0) return { label: 'Paid', color: '#10b981' };
  if (paid > 0) return { label: 'Partial', color: '#f97316' };
  return { label: 'Unpaid', color: '#ef4444' };
};
const scheduleBadge = (s) => {
  const map = { weekly: { label: 'Weekly', color: '#8b5cf6' }, 'bi-weekly': { label: 'Bi-Weekly', color: '#f59e0b' }, monthly: { label: 'Monthly', color: '#3b82f6' } };
  return map[s] || map.monthly;
};

const Scheduler = () => {
  const [tab, setTab] = useState(0);

  // === Generate Tab (Wizard) ===
  const [wizardStep, setWizardStep] = useState(0);
  const [invoiceType, setInvoiceType] = useState('provider');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingCollections, setPendingCollections] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingPage, setPendingPage] = useState(0);
  const [pendingRpp, setPendingRpp] = useState(20);

  // === Missing Collections Tab ===
  const [missingMonth, setMissingMonth] = useState(new Date().toISOString().slice(0, 7));
  const [missingData, setMissingData] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingSearch, setMissingSearch] = useState('');
  const [addCollectionTarget, setAddCollectionTarget] = useState(null);

  // === History Tab ===
  const [historyType, setHistoryType] = useState('provider');
  const [providerInvoices, setProviderInvoices] = useState([]);
  const [contractorInvoices, setContractorInvoices] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRpp, setHistoryRpp] = useState(20);
  const [historySearch, setHistorySearch] = useState('');
  const [historyLoading, setHistoryLoading] = useState(true);

  // === Dialogs ===
  const [viewInvoice, setViewInvoice] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [gearAnchor, setGearAnchor] = useState(null);
  const [gearTarget, setGearTarget] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const [pi, ci] = await Promise.all([providerInvoicesAPI.getAll({ limit: 1000 }), contractorInvoicesAPI.getAll({ limit: 1000 })]);
      setProviderInvoices(Array.isArray(pi.data) ? pi.data : pi.data?.data || []);
      setContractorInvoices(Array.isArray(ci.data) ? ci.data : ci.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Invoice Hub | MyPayPlus'; }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // === Generate: Find uninvoiced collections ===
  const loadPending = async () => {
    setLoading(true); setPendingCollections([]); setSelectedIds([]); setPendingPage(0);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const api = invoiceType === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      const res = await api.getPendingCollections(params);
      const data = Array.isArray(res.data) ? res.data : [];
      setPendingCollections(data);
      if (data.length > 0) setWizardStep(1);
      else toast.info('No uninvoiced collections found for the selected period');
    } catch (e) { toast.error('Failed to load pending collections'); }
    finally { setLoading(false); }
  };

  const handleSelectAll = (checked) => setSelectedIds(checked ? filtered.map(c => c._id || c.id) : []);
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleBulkGenerate = async () => {
    if (!selectedIds.length) { toast.warning('No collections selected'); return; }
    setGenerating(true);
    try {
      const api = invoiceType === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      const res = await api.bulkGenerate({ collectionIds: selectedIds });
      toast.success(`${res.data.generated} invoice(s) generated${res.data.errors > 0 ? `, ${res.data.errors} errors` : ''}`);
      setPendingCollections([]); setSelectedIds([]); setWizardStep(0); loadHistory();
    } catch (e) { toast.error(e.response?.data?.message || 'Bulk generation failed'); }
    finally { setGenerating(false); }
  };

  // === Missing Collections ===
  const loadMissing = async () => {
    if (!missingMonth) return;
    setMissingLoading(true);
    try {
      const res = await providerInvoicesAPI.getMissingCollections({ month: missingMonth });
      setMissingData(Array.isArray(res.data) ? res.data : []);
    } catch (e) { toast.error('Failed to load missing collections'); console.error(e); }
    finally { setMissingLoading(false); }
  };

  const handleAddCollection = async () => {
    if (!addCollectionTarget) return;
    const t = addCollectionTarget;
    try {
      await collectionsAPI.create({
        provider: t.providerId,
        contractor: t.contractorId || undefined,
        collectionPeriod: missingMonth + '-01',
        services: [{ service: '', serviceName: '', collectionAmount: 0, billingRate: 0, billingAmount: 0, commissionRate: 0, commission: 0, profit: 0, isFixed: false }],
        totalCollectionAmount: 0, totalBillingAmount: 0, totalCommission: 0, totalProfit: 0
      });
      toast.success('Collection created — edit it in Collections page to add service details');
      setAddCollectionTarget(null);
      loadMissing();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create collection'); }
  };

  // === History: Delete ===
  const handleDeleteInvoice = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const api = deleteTarget._type === 'provider' ? providerInvoicesAPI : contractorInvoicesAPI;
      await api.delete(deleteTarget._id || deleteTarget.id);
      toast.success('Invoice deleted successfully');
      setDeleteTarget(null); setViewInvoice(null); loadHistory();
    } catch (e) { toast.error(e.response?.data?.message || 'Cannot delete invoice — it may have payments linked'); }
    finally { setDeleting(false); }
  };

  // === Filter logic ===
  const filtered = pendingCollections.filter(c => {
    const q = search.toLowerCase();
    return [fullName(c.provider), fullName(c.contractor), c.collectionId || ''].some(v => (v || '').toLowerCase().includes(q));
  });

  const filteredMissing = missingData.filter(m => {
    if (!missingSearch) return true;
    const q = missingSearch.toLowerCase();
    return [fullName(m.provider), fullName(m.contractor), m.invoiceSchedule || ''].some(v => (v || '').toLowerCase().includes(q));
  });

  const missingColumns = useMemo(() => [
    { id: 'provider', label: 'Provider', getValue: r => fullName(r.provider) || '' },
    { id: 'contractor', label: 'Assigned Contractors', getValue: r => r.contractor ? fullName(r.contractor) : (r.assignedContractors || []).map(a => fullName(a.contractor)).join(', ') },
    { id: 'missingPeriod', label: 'Missing Period', getValue: r => r.missingPeriod || '' },
    { id: 'invoiceSchedule', label: 'Invoice Schedule', getValue: r => r.invoiceSchedule || '' },
    { id: 'lastInvoiceDate', label: 'Last Invoice Generated', getValue: r => r.lastInvoiceDate || '' },
    { id: 'status', label: 'Provider Status', getValue: r => r.isActive ? 'Active' : 'Inactive' },
  ], []);

  const { sortedData: sortedMissing, renderHeader: renderMissingHeader, renderFilterPopover: renderMissingFilterPopover, renderClearFilters: renderMissingClearFilters } = useTableSort(missingColumns, filteredMissing, { defaultSort: 'provider', defaultDir: 'asc' });

  const currentHistoryList = historyType === 'provider' ? providerInvoices : contractorInvoices;
  const filteredHistory = currentHistoryList.filter(inv => {
    if (!historySearch) return true;
    const q = historySearch.toLowerCase();
    const name = historyType === 'provider' ? fullName(inv.provider) : fullName(inv.contractor);
    return [inv.invoiceNumber || '', name || '', inv.status || ''].some(v => v.toLowerCase().includes(q));
  });

  // === Stats ===
  const allInvoices = [...providerInvoices, ...contractorInvoices];
  const stats = {
    total: allInvoices.length,
    amount: allInvoices.reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
    pending: allInvoices.filter(i => ['generated', 'sent'].includes(i.status)).length,
    paid: allInvoices.filter(i => i.status === 'paid').length,
  };

  // === Grouped preview for wizard Step 2 ===
  const selectedCollections = filtered.filter(c => selectedIds.includes(c._id || c.id));
  const groupedPreview = useMemo(() => {
    const groups = {};
    for (const c of selectedCollections) {
      const pId = c.provider?.id || c.providerId;
      const schedule = c.provider?.invoiceSchedule || 'monthly';
      const period = c.collectionPeriod ? new Date(c.collectionPeriod) : new Date();
      let groupKey;
      if (schedule === 'bi-weekly') {
        const day = period.getDate();
        const half = day <= 15 ? '1st-15th' : '16th-' + new Date(period.getFullYear(), period.getMonth() + 1, 0).getDate();
        groupKey = `${pId}-${period.getFullYear()}-${String(period.getMonth() + 1).padStart(2, '0')}-${half}`;
      } else if (schedule === 'weekly') {
        const jan1 = new Date(period.getFullYear(), 0, 1);
        const weekNum = Math.ceil(((period - jan1) / 86400000 + jan1.getDay() + 1) / 7);
        groupKey = `${pId}-W${weekNum}`;
      } else {
        groupKey = `${pId}-${period.getFullYear()}-${String(period.getMonth() + 1).padStart(2, '0')}`;
      }
      if (!groups[groupKey]) {
        groups[groupKey] = {
          provider: c.provider,
          schedule,
          periodLabel: schedule === 'bi-weekly'
            ? `${period.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} (${period.getDate() <= 15 ? '1st\u201315th' : '16th\u2013' + new Date(period.getFullYear(), period.getMonth() + 1, 0).getDate() + 'th'})`
            : schedule === 'weekly'
              ? `Week of ${period.toLocaleDateString()}`
              : period.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          collections: [],
          total: 0
        };
      }
      groups[groupKey].collections.push(c);
      groups[groupKey].total += parseFloat(c.totalBillingAmount) || 0;
    }
    return Object.values(groups);
  // eslint-disable-next-line
  }, [selectedIds, filtered]);

  const selectedTotal = selectedCollections.reduce((s, c) => s + (parseFloat(c.totalBillingAmount) || 0), 0);

  // === Gear menu handlers ===
  const handleGearOpen = (e, inv) => { setGearAnchor(e.currentTarget); setGearTarget(inv); };
  const handleGearClose = () => { setGearAnchor(null); setGearTarget(null); };
  const handleEmailContractor = () => {
    if (gearTarget) {
      const email = gearTarget.provider?.email || gearTarget.contractor?.email;
      if (email) window.open(`mailto:${email}?subject=Invoice ${encodeURIComponent(gearTarget.invoiceNumber || '')}`);
      else toast.warning('No email address found');
    }
    handleGearClose();
  };
  const handleViewHistory = () => {
    if (gearTarget) setHistoryDialog(gearTarget);
    handleGearClose();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Invoice Hub</Typography>
          <Typography variant="body2" color="text.secondary">Bulk generate invoices, find missing collections, and track invoice history</Typography>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Invoices', value: stats.total, icon: <Receipt />, color: '#3b82f6' },
          { label: 'Total Revenue', value: fmt(stats.amount), icon: <TrendingUp />, color: '#10b981' },
          { label: 'Pending', value: stats.pending, icon: <CalendarToday />, color: '#f59e0b' },
          { label: 'Paid', value: stats.paid, icon: <CheckCircle />, color: '#8b5cf6' },
        ].map((s, i) => (
          <Grid item xs={6} md={3} key={i}>
            <Card sx={{ borderTop: `3px solid ${s.color}` }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 }, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 44, height: 44, bgcolor: alpha(s.color, 0.1), color: s.color }}>{s.icon}</Avatar>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: s.color }}>{s.value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ px: 3, pt: 1, borderBottom: '1px solid', borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.85rem' },
            '& .MuiTabs-indicator': { bgcolor: '#3b82f6', height: 3, borderRadius: 1.5 } }}>
          <Tab icon={<PlayArrow sx={{ fontSize: 18 }} />} iconPosition="start" label="Generate Invoices" />
          <Tab icon={<WarningAmber sx={{ fontSize: 18 }} />} iconPosition="start" label={`Missing Collections${missingData.length ? ` (${missingData.length})` : ''}`} />
          <Tab icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" label="Invoice History" />
        </Tabs>

        {/* ==================== TAB 1: GENERATE INVOICES (WIZARD) ==================== */}
        <TabPanel value={tab} index={0}>
          <CardContent>
            {/* Wizard Stepper */}
            <Stepper activeStep={wizardStep} sx={{ mb: 3 }}>
              <Step><StepLabel>Find Collections</StepLabel></Step>
              <Step><StepLabel>Select &amp; Review</StepLabel></Step>
              <Step><StepLabel>Generate Invoices</StepLabel></Step>
            </Stepper>

            {/* --- Step 0: Search Criteria --- */}
            {wizardStep === 0 && (
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 2 }}>Step 1: Find Uninvoiced Collections</Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Invoice Type</Typography>
                    <ToggleButtonGroup size="small" exclusive value={invoiceType}
                      onChange={(_, v) => { if (v) setInvoiceType(v); }}
                      sx={{ '& .MuiToggleButton-root': { px: 2, py: 0.8, fontSize: '0.8rem', fontWeight: 700, textTransform: 'none' } }}>
                      <ToggleButton value="provider"><Business sx={{ fontSize: 16, mr: 0.5 }} /> Provider</ToggleButton>
                      <ToggleButton value="contractor"><People sx={{ fontSize: 16, mr: 0.5 }} /> Contractor</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Billing Period Start</Typography>
                    <TextField size="small" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Billing Period End</Typography>
                    <TextField size="small" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }} sx={{ width: 170 }} />
                  </Box>
                  <Button variant="contained" onClick={loadPending} startIcon={<Search />} disabled={loading}
                    sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, py: 1 }}>
                    {loading ? 'Searching...' : 'Find Collections'}
                  </Button>
                </Box>
                {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
                {!loading && !pendingCollections.length && (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Receipt sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                    <Typography color="text.secondary" sx={{ fontWeight: 600 }}>Select billing period and click Find Collections</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.82rem', mt: 0.5 }}>Only uninvoiced collections will appear</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* --- Step 1: Select & Review --- */}
            {wizardStep === 1 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button size="small" startIcon={<ArrowBack />} onClick={() => setWizardStep(0)} sx={{ fontWeight: 600 }}>Back</Button>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      {filtered.length} Uninvoiced Collection{filtered.length !== 1 ? 's' : ''}
                    </Typography>
                    <Chip label={`${selectedIds.length} selected`} size="small"
                      sx={{ fontWeight: 700, bgcolor: alpha('#3b82f6', 0.1), color: '#2563eb' }} />
                    {selectedIds.length > 0 && (
                      <Chip label={`Total: ${fmt(selectedTotal)}`} size="small"
                        sx={{ fontWeight: 700, bgcolor: alpha('#10b981', 0.1), color: '#059669' }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField size="small" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                      sx={{ width: 200 }} />
                    <Button variant="contained" onClick={() => { if (selectedIds.length) setWizardStep(2); else toast.warning('Select at least one collection'); }}
                      endIcon={<ArrowForward />} disabled={!selectedIds.length}
                      sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700 }}>
                      Review ({selectedIds.length})
                    </Button>
                  </Box>
                </Box>

                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selectedIds.length === filtered.length && filtered.length > 0}
                            indeterminate={selectedIds.length > 0 && selectedIds.length < filtered.length}
                            onChange={e => handleSelectAll(e.target.checked)} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Collection ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Provider</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Schedule</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Contractor</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Collected</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Billing</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered.slice(pendingPage * pendingRpp, pendingPage * pendingRpp + pendingRpp).map(c => {
                        const cId = c._id || c.id;
                        const sb = scheduleBadge(c.provider?.invoiceSchedule || 'monthly');
                        return (
                          <TableRow key={cId} hover onClick={() => toggleSelect(cId)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#3b82f6', 0.03) },
                              bgcolor: selectedIds.includes(cId) ? alpha('#3b82f6', 0.06) : 'inherit' }}>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={selectedIds.includes(cId)}
                                onClick={e => e.stopPropagation()} onChange={() => toggleSelect(cId)} />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#d97706', fontFamily: 'monospace' }}>{c.collectionId}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.82rem' }}>{c.collectionPeriod ? new Date(c.collectionPeriod).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '\u2014'}</Typography>
                            </TableCell>
                            <TableCell><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{fullName(c.provider) || '\u2014'}</Typography></TableCell>
                            <TableCell>
                              <Chip label={sb.label} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha(sb.color, 0.1), color: sb.color }} />
                            </TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{fullName(c.contractor) || '\u2014'}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{fmt(c.totalCollectionAmount)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#d97706' }}>{fmt(c.totalBillingAmount)}</Typography></TableCell>
                            <TableCell>
                              <Chip label="Uninvoiced" size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha('#ef4444', 0.1), color: '#ef4444' }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination component="div" count={filtered.length} page={pendingPage} rowsPerPage={pendingRpp}
                  onPageChange={(_, p) => setPendingPage(p)} onRowsPerPageChange={e => { setPendingRpp(+e.target.value); setPendingPage(0); }}
                  rowsPerPageOptions={[10, 20, 50, 100]} />
              </Box>
            )}

            {/* --- Step 2: Preview & Generate --- */}
            {wizardStep === 2 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button size="small" startIcon={<ArrowBack />} onClick={() => setWizardStep(1)} sx={{ fontWeight: 600 }}>Back to Selection</Button>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Review: {groupedPreview.length} Invoice{groupedPreview.length !== 1 ? 's' : ''} will be created
                    </Typography>
                  </Box>
                  <Button variant="contained" onClick={handleBulkGenerate} disabled={generating}
                    startIcon={generating ? <Refresh className="spin" /> : <CheckCircle />}
                    sx={{ background: 'linear-gradient(135deg, #10b981, #059669)', fontWeight: 700, px: 3 }}>
                    {generating ? 'Generating...' : `Confirm & Generate (${groupedPreview.length})`}
                  </Button>
                </Box>

                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 3 }}>
                  Invoices are grouped by provider and their invoice schedule. Bi-weekly providers get separate invoices for 1st–15th and 16th–end of month.
                  Invoice and due dates are set per the provider's due date setting: <strong>1st/15th</strong> = Invoice 1st, Pay 15th; <strong>15th/1st</strong> = Invoice 15th, Pay 1st.
                </Typography>

                {groupedPreview.map((group, gIdx) => {
                  const sb = scheduleBadge(group.schedule);
                  return (
                    <Card key={gIdx} variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
                      <CardContent sx={{ pb: '12px !important' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6', fontSize: '0.85rem', fontWeight: 800 }}>
                              {(fullName(group.provider) || '?')[0]}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{fullName(group.provider) || 'Unknown'}</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{group.periodLabel}</Typography>
                            </Box>
                            <Chip label={sb.label} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha(sb.color, 0.1), color: sb.color }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#10b981' }}>{fmt(group.total)}</Typography>
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>Collection</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>Period</TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>Contractor</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>Collected</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.72rem', py: 0.5 }}>Billing</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.collections.map(c => (
                                <TableRow key={c._id || c.id}>
                                  <TableCell sx={{ py: 0.5 }}><Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600, color: '#d97706' }}>{c.collectionId}</Typography></TableCell>
                                  <TableCell sx={{ py: 0.5 }}><Typography sx={{ fontSize: '0.78rem' }}>{c.collectionPeriod ? new Date(c.collectionPeriod).toLocaleDateString() : '\u2014'}</Typography></TableCell>
                                  <TableCell sx={{ py: 0.5 }}><Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{fullName(c.contractor) || '\u2014'}</Typography></TableCell>
                                  <TableCell align="right" sx={{ py: 0.5 }}><Typography sx={{ fontSize: '0.78rem' }}>{fmt(c.totalCollectionAmount)}</Typography></TableCell>
                                  <TableCell align="right" sx={{ py: 0.5 }}><Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{fmt(c.totalBillingAmount)}</Typography></TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  );
                })}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, p: 2, bgcolor: alpha('#10b981', 0.04), borderRadius: 2, border: '1px solid', borderColor: alpha('#10b981', 0.2) }}>
                  <Typography sx={{ fontWeight: 700 }}>Grand Total ({selectedIds.length} collections \u2192 {groupedPreview.length} invoices)</Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#10b981' }}>{fmt(selectedTotal)}</Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* ==================== TAB 2: MISSING COLLECTIONS ==================== */}
        <TabPanel value={tab} index={1}>
          <CardContent>
            {/* Explanation Box */}
            <Box sx={{ mb: 3, p: 2, bgcolor: alpha('#3b82f6', 0.04), borderRadius: 2, border: '1px solid', borderColor: alpha('#3b82f6', 0.15) }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Info sx={{ fontSize: 18, color: '#3b82f6', mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e40af', mb: 0.5 }}>How Missing Collections are Detected</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.6 }}>
                    This tool compares <strong>active providers</strong> and their <strong>assigned contractors</strong> against existing collections for the selected month.
                    If a provider-contractor pair has no collection entry for that period, it appears here as &ldquo;missing.&rdquo;
                    For providers with multiple contractors, each contractor is checked individually.
                    Only active providers and active contractor assignments are considered.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Select Month</Typography>
                <TextField size="small" type="month" value={missingMonth} onChange={e => setMissingMonth(e.target.value)} sx={{ width: 180 }} />
              </Box>
              <Button variant="contained" onClick={loadMissing} startIcon={<Search />} disabled={missingLoading}
                sx={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', fontWeight: 700, py: 1 }}>
                {missingLoading ? 'Checking...' : 'Find Missing'}
              </Button>
              {missingData.length > 0 && (
                <TextField size="small" placeholder="Search..." value={missingSearch} onChange={e => setMissingSearch(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                  sx={{ width: 200, ml: 'auto' }} />
              )}
            </Box>

            {missingLoading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

            {!missingLoading && missingData.length > 0 && (
              <>
                <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha('#f59e0b', 0.06), borderRadius: 2, border: '1px solid', borderColor: alpha('#f59e0b', 0.2) }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#b45309' }}>
                    <WarningAmber sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                    {filteredMissing.length} active provider{filteredMissing.length !== 1 ? '-contractor pair' : ''}{filteredMissing.length !== 1 ? 's' : ''} missing collections for {new Date(missingMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </Typography>
                </Box>

                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  {renderMissingFilterPopover()}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 0.5 }}>{renderMissingClearFilters()}</Box>
                  <Table size="small">
                    {renderMissingHeader(<TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Actions</TableCell>)}
                    <TableBody>
                      {sortedMissing.map((m, idx) => {
                        const sb = scheduleBadge(m.invoiceSchedule);
                        return (
                          <TableRow key={idx} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{fullName(m.provider) || '\u2014'}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: 'monospace' }}>{m.provider?.providerId}</Typography>
                            </TableCell>
                            <TableCell>
                              {m.contractor ? (
                                <Chip label={fullName(m.contractor)} size="small" sx={{ height: 24, fontSize: '0.75rem', fontWeight: 600 }} />
                              ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {(m.assignedContractors || []).map((a, i) => (
                                    <Chip key={i} label={fullName(a.contractor)} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }} />
                                  ))}
                                  {(!m.assignedContractors || m.assignedContractors.length === 0) && <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>None</Typography>}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                {m.missingPeriod ? new Date(m.missingPeriod).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '\u2014'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={sb.label} size="small" sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: alpha(sb.color, 0.1), color: sb.color }} />
                            </TableCell>
                            <TableCell>
                              {m.lastInvoiceDate ? (
                                <Box>
                                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                                    {new Date(m.lastInvoiceDate).toLocaleDateString()}
                                  </Typography>
                                  {m.lastInvoicePeriod && (
                                    <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                      Period: {m.lastInvoicePeriod}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }}>No invoices yet</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip label={m.isActive ? 'Active' : 'Inactive'} size="small"
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700,
                                  bgcolor: alpha(m.isActive ? '#10b981' : '#94a3b8', 0.1), color: m.isActive ? '#10b981' : '#94a3b8' }} />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Add Collection for this period">
                                <Button size="small" variant="contained" startIcon={<Add sx={{ fontSize: 16 }} />}
                                  onClick={() => setAddCollectionTarget(m)}
                                  sx={{ fontSize: '0.72rem', fontWeight: 700, py: 0.3, px: 1.5, textTransform: 'none',
                                    background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                                  Add Collection
                                </Button>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {!missingLoading && missingData.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <CheckCircle sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                  {missingMonth ? 'No missing collections found \u2014 all active providers have collections' : 'Select a month to check for missing collections'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </TabPanel>

        {/* ==================== TAB 3: INVOICE HISTORY ==================== */}
        <TabPanel value={tab} index={2}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <ToggleButtonGroup size="small" exclusive value={historyType}
                onChange={(_, v) => { if (v) { setHistoryType(v); setHistoryPage(0); } }}
                sx={{ '& .MuiToggleButton-root': { px: 2.5, py: 0.8, fontSize: '0.8rem', fontWeight: 700, textTransform: 'none' } }}>
                <ToggleButton value="provider"><Business sx={{ fontSize: 16, mr: 0.5 }} /> Provider Invoices ({providerInvoices.length})</ToggleButton>
                <ToggleButton value="contractor"><People sx={{ fontSize: 16, mr: 0.5 }} /> Contractor Invoices ({contractorInvoices.length})</ToggleButton>
              </ToggleButtonGroup>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField size="small" placeholder="Search invoices..." value={historySearch}
                  onChange={e => { setHistorySearch(e.target.value); setHistoryPage(0); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }}
                  sx={{ width: 240 }} />
                <Tooltip title="Refresh"><IconButton size="small" onClick={loadHistory}><Refresh /></IconButton></Tooltip>
              </Box>
            </Box>

            {historyLoading ? (
              <Box>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
            ) : !filteredHistory.length ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Receipt sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                <Typography color="text.secondary" sx={{ fontWeight: 600 }}>No {historyType} invoices found</Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Invoice #</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{historyType === 'provider' ? 'Provider' : 'Contractor'}</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Type</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Paid</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Balance</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Payment</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredHistory.slice(historyPage * historyRpp, historyPage * historyRpp + historyRpp).map(inv => {
                        const invId = inv._id || inv.id;
                        const name = historyType === 'provider' ? fullName(inv.provider) : fullName(inv.contractor);
                        const total = parseFloat(inv.total) || 0;
                        const paid = parseFloat(inv.amountPaid) || 0;
                        const balance = total - paid;
                        const pc = paymentChip(inv);
                        return (
                          <TableRow key={invId} hover>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: historyType === 'provider' ? '#3b82f6' : '#8b5cf6', fontFamily: 'monospace' }}>
                                {inv.invoiceNumber}
                              </Typography>
                            </TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{name || '\u2014'}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.78rem' }}>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '\u2014'}</Typography></TableCell>
                            <TableCell><Typography sx={{ fontSize: '0.78rem' }}>
                              {(() => {
                                const ps = inv.periodStart ? new Date(inv.periodStart).toLocaleDateString() : null;
                                const pe = inv.periodEnd ? new Date(inv.periodEnd).toLocaleDateString() : null;
                                if (!ps && !pe) return '\u2014';
                                if (ps && pe && ps === pe) return ps;
                                if (ps && !pe) return ps;
                                return `${ps || '\u2014'} \u2013 ${pe}`;
                              })()}
                            </Typography></TableCell>
                            <TableCell>
                              <Chip label={inv.generationType === 'bulk' ? 'Bulk' : 'Manual'} size="small"
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700,
                                  bgcolor: alpha(inv.generationType === 'bulk' ? '#8b5cf6' : '#64748b', 0.1),
                                  color: inv.generationType === 'bulk' ? '#8b5cf6' : '#64748b' }} />
                            </TableCell>
                            <TableCell align="right"><Typography sx={{ fontWeight: 700, fontSize: '0.78rem' }}>{fmt(total)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontSize: '0.78rem', color: '#10b981' }}>{fmt(paid)}</Typography></TableCell>
                            <TableCell align="right"><Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: balance > 0 ? '#ef4444' : '#10b981' }}>{fmt(balance)}</Typography></TableCell>
                            <TableCell>
                              <Chip label={pc.label} size="small" sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha(pc.color, 0.1), color: pc.color }} />
                            </TableCell>
                            <TableCell>
                              <Chip label={inv.status || 'generated'} size="small"
                                sx={{ height: 22, fontWeight: 600, fontSize: '0.68rem', textTransform: 'capitalize', bgcolor: alpha(statusColor(inv.status), 0.1), color: statusColor(inv.status) }} />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="View Invoice">
                                  <IconButton size="small" onClick={() => setViewInvoice({ ...inv, _type: historyType })}>
                                    <Visibility sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Options">
                                  <IconButton size="small" onClick={(e) => handleGearOpen(e, { ...inv, _type: historyType })}>
                                    <SettingsIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination component="div" count={filteredHistory.length} page={historyPage} rowsPerPage={historyRpp}
                  onPageChange={(_, p) => setHistoryPage(p)} onRowsPerPageChange={e => { setHistoryRpp(+e.target.value); setHistoryPage(0); }}
                  rowsPerPageOptions={[10, 20, 50, 100]} />
              </>
            )}
          </CardContent>
        </TabPanel>
      </Card>

      {/* ==================== GEAR MENU ==================== */}
      <Menu anchorEl={gearAnchor} open={!!gearAnchor} onClose={handleGearClose}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}>
        <MenuItem onClick={() => { setViewInvoice(gearTarget); handleGearClose(); }}>
          <ListItemIcon><Visibility sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primary="View Invoice" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
        </MenuItem>
        <MenuItem onClick={handleEmailContractor}>
          <ListItemIcon><Email sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primary="Email" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
        </MenuItem>
        <MenuItem onClick={handleViewHistory}>
          <ListItemIcon><EditNote sx={{ fontSize: 18 }} /></ListItemIcon>
          <ListItemText primary="Edit History" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
        </MenuItem>
        <Divider />
        {gearTarget && (parseFloat(gearTarget.amountPaid) || 0) === 0 && (
          <MenuItem onClick={() => { setDeleteTarget(gearTarget); handleGearClose(); }} sx={{ color: '#ef4444' }}>
            <ListItemIcon><Delete sx={{ fontSize: 18, color: '#ef4444' }} /></ListItemIcon>
            <ListItemText primary="Delete Invoice" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
          </MenuItem>
        )}
      </Menu>

      {/* ==================== VIEW INVOICE DIALOG ==================== */}
      <Dialog open={!!viewInvoice} onClose={() => setViewInvoice(null)} maxWidth="lg" fullWidth>
        {viewInvoice && (() => {
          const inv = viewInvoice;
          const total = parseFloat(inv.total) || 0;
          const paid = parseFloat(inv.amountPaid) || 0;
          const balance = total - paid;
          const pc = paymentChip(inv);
          const canDel = paid === 0;
          const name = inv._type === 'provider' ? fullName(inv.provider) : fullName(inv.contractor);
          const items = inv.items || inv.lineItems || [];
          const collections = inv.collections || [];
          return (
            <>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>Invoice {inv.invoiceNumber}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inv._type === 'provider' ? 'Provider' : 'Contractor'} Invoice \u2014 {name}
                  </Typography>
                </Box>
                <IconButton onClick={() => setViewInvoice(null)}><Close /></IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Invoice Date</Typography>
                    <Typography sx={{ fontWeight: 600 }}>{inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : '\u2014'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Period</Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {(() => {
                        const ps = inv.periodStart ? new Date(inv.periodStart).toLocaleDateString() : null;
                        const pe = inv.periodEnd ? new Date(inv.periodEnd).toLocaleDateString() : null;
                        if (!ps && !pe) return '\u2014';
                        if (ps && pe && ps === pe) return ps;
                        if (ps && !pe) return ps;
                        return `${ps || '\u2014'} \u2013 ${pe}`;
                      })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Status</Typography>
                    <Chip label={inv.status || 'generated'} size="small" sx={{ mt: 0.5, fontWeight: 700, textTransform: 'capitalize', bgcolor: alpha(statusColor(inv.status), 0.12), color: statusColor(inv.status) }} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Payment</Typography>
                    <Chip label={pc.label} size="small" sx={{ mt: 0.5, fontWeight: 700, bgcolor: alpha(pc.color, 0.12), color: pc.color }} />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: 'Total', value: fmt(total), color: '#3b82f6' },
                    { label: 'Paid', value: fmt(paid), color: '#10b981' },
                    { label: 'Balance', value: fmt(balance), color: balance > 0 ? '#ef4444' : '#10b981' },
                  ].map((s, i) => (
                    <Grid item xs={4} key={i}>
                      <Card variant="outlined" sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{s.label}</Typography>
                        <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: s.color }}>{s.value}</Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                {items.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>Line Items</Typography>
                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Rate</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {items.map((it, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{it.description || it.serviceName || '\u2014'}</TableCell>
                              <TableCell align="right">{fmt(it.rate || it.unitPrice)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(it.amount || it.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                {collections.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1 }}>Linked Collections</Typography>
                    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Collection ID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Collected</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Billing</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {collections.map((col, idx) => (
                            <TableRow key={idx}>
                              <TableCell><Typography sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>{col.collectionId}</Typography></TableCell>
                              <TableCell>{col.collectionPeriod ? new Date(col.collectionPeriod).toLocaleDateString() : '\u2014'}</TableCell>
                              <TableCell align="right">{fmt(col.totalCollectionAmount)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(col.totalBillingAmount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                {inv.sentAt && (
                  <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha('#3b82f6', 0.04), borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                      <Email sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                      Sent on {new Date(inv.sentAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                {inv.notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>Notes</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{inv.notes}</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2 }}>
                {canDel && (
                  <Button color="error" variant="outlined" startIcon={<Delete />} onClick={() => setDeleteTarget({ ...inv })}>Delete Invoice</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={() => setViewInvoice(null)} variant="contained">Close</Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ==================== EDIT HISTORY DIALOG ==================== */}
      <Dialog open={!!historyDialog} onClose={() => setHistoryDialog(null)} maxWidth="sm" fullWidth>
        {historyDialog && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Invoice History \u2014 {historyDialog.invoiceNumber}</Typography>
              <IconButton onClick={() => setHistoryDialog(null)}><Close /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <List dense>
                <ListItem>
                  <ListItemIcon><Receipt sx={{ fontSize: 18, color: '#3b82f6' }} /></ListItemIcon>
                  <ListItemText
                    primary="Invoice Created"
                    secondary={historyDialog.createdAt ? new Date(historyDialog.createdAt).toLocaleString() : '\u2014'}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.78rem' }}
                  />
                </ListItem>
                {historyDialog.sentAt && (
                  <ListItem>
                    <ListItemIcon><Email sx={{ fontSize: 18, color: '#f59e0b' }} /></ListItemIcon>
                    <ListItemText
                      primary="Email Sent"
                      secondary={new Date(historyDialog.sentAt).toLocaleString()}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                  </ListItem>
                )}
                {(parseFloat(historyDialog.amountPaid) || 0) > 0 && (
                  <ListItem>
                    <ListItemIcon><PaymentIcon sx={{ fontSize: 18, color: '#10b981' }} /></ListItemIcon>
                    <ListItemText
                      primary={`Payment Recorded \u2014 ${fmt(historyDialog.amountPaid)}`}
                      secondary={historyDialog.updatedAt ? new Date(historyDialog.updatedAt).toLocaleString() : '\u2014'}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                  </ListItem>
                )}
                {historyDialog.pdfPath && (
                  <ListItem>
                    <ListItemIcon><Description sx={{ fontSize: 18, color: '#8b5cf6' }} /></ListItemIcon>
                    <ListItemText
                      primary="PDF Generated"
                      secondary="Invoice PDF available for download"
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                  </ListItem>
                )}
                {historyDialog.status === 'cancelled' && (
                  <ListItem>
                    <ListItemIcon><Close sx={{ fontSize: 18, color: '#ef4444' }} /></ListItemIcon>
                    <ListItemText
                      primary="Invoice Cancelled"
                      secondary={historyDialog.updatedAt ? new Date(historyDialog.updatedAt).toLocaleString() : '\u2014'}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem', color: '#ef4444' }}
                      secondaryTypographyProps={{ fontSize: '0.78rem' }}
                    />
                  </ListItem>
                )}
              </List>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setHistoryDialog(null)} variant="contained">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ==================== ADD COLLECTION CONFIRMATION ==================== */}
      <Dialog open={!!addCollectionTarget} onClose={() => setAddCollectionTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add Collection?</DialogTitle>
        <DialogContent>
          <Typography>Create a collection for:</Typography>
          <Box sx={{ mt: 1.5, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontWeight: 700 }}>{fullName(addCollectionTarget?.provider)}</Typography>
            {addCollectionTarget?.contractor && (
              <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>Contractor: {fullName(addCollectionTarget?.contractor)}</Typography>
            )}
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              Period: {missingMonth ? new Date(missingMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '\u2014'}
            </Typography>
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: '0.82rem', color: 'text.secondary' }}>
            A draft collection will be created. You can edit it in the Collections page to add service details and amounts.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddCollectionTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCollection} startIcon={<Add />}
            sx={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>Create Collection</Button>
        </DialogActions>
      </Dialog>

      {/* ==================== DELETE CONFIRMATION ==================== */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Invoice?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete invoice <strong>{deleteTarget?.invoiceNumber}</strong>?</Typography>
          <Typography sx={{ mt: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
            This will unlink all collections from this invoice and they will become available for new invoice generation. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteInvoice} disabled={deleting}
            startIcon={deleting ? <Refresh className="spin" /> : <Delete />}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Scheduler;
